import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWarehouse } from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS } from '../../utils/colors';
import { useTranslation } from 'react-i18next';

const EDITABLE_FIELDS = ['stock_quantity', 'minimum_stock', 'pending_order'];

const isDirtyValue = (edited, original) => {
  if (edited === undefined || edited === null || edited === '') return false;
  const editedNum = Number(edited);
  const origNum = Number(original ?? 0);
  if (Number.isFinite(editedNum) && Number.isFinite(origNum)) return editedNum !== origNum;
  return String(edited) !== String(original ?? '');
};

const BulkEditWarehouseRows = ({
  product,
  edits = {},
  onCellChange,
  errors = {},
}) => {
  const { t } = useTranslation();
  const breakdown = Array.isArray(product?.warehouse_breakdown) ? product.warehouse_breakdown : [];
  const perWH = t('bulkInventoryEdit.perWarehouseTable', { returnObjects: true });

  if (breakdown.length === 0) {
    return (
      <div className="text-sm py-2" style={{ color: UI_COLORS.TEXT.MUTED }}>
        {perWH.noWarehouses}
      </div>
    );
  }

  const renderInput = (warehouseId, field, originalValue) => {
    const edited = edits?.[warehouseId]?.[field];
    // Show the user's typed value (including "" while clearing and "0") — never override user input.
    const displayValue = edited !== undefined && edited !== null ? edited : String(originalValue ?? '');
    const dirty = isDirtyValue(edited, originalValue);
    const errKey = `${warehouseId}.${field}`;
    const hasError = !!errors?.[errKey];

    return (
      <div className="flex flex-col">
        <input
          type="number"
          step="0.01"
          min="0"
          value={displayValue}
          onChange={(e) => onCellChange?.(warehouseId, field, e.target.value)}
          onWheel={(e) => e.target.blur()}
          className="w-full px-2 py-1.5 rounded-md border text-sm font-semibold focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          style={{
            borderColor: hasError ? UI_COLORS.STATUS.ERROR : (dirty ? '#3b82f6' : UI_COLORS.FORMS.INPUT.border),
            backgroundColor: UI_COLORS.FORMS.INPUT.background,
            borderLeftWidth: dirty ? 4 : 1,
          }}
        />
        {hasError && (
          <span className="text-[10px] mt-0.5" style={{ color: UI_COLORS.STATUS.ERROR }}>
            {errors[errKey]}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="rounded-lg overflow-hidden border" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
      <div
        className="grid gap-3 px-4 py-2 text-xs font-semibold uppercase tracking-wider"
        style={{
          gridTemplateColumns: '1.4fr 1fr 1fr 1fr',
          backgroundColor: UI_COLORS.BACKGROUNDS?.SECTION || '#f8fafc',
          color: UI_COLORS.TEXT.MUTED,
        }}
      >
        <div>{perWH.warehouse}</div>
        <div>{perWH.stockQty}</div>
        <div>{perWH.minStock}</div>
        <div>{perWH.pendingOrder}</div>
      </div>
      <div className="bg-white">
        {breakdown.map((wh) => {
          const warehouseId = wh.warehouse_id || wh.id;
          const warehouseName = wh.warehouse_name || wh.name || t('bulkInventoryEdit.fallbacks.warehouse', { id: String(warehouseId) });
          const isLow = !!wh.is_low_stock;
          const hasAnyDirty = EDITABLE_FIELDS.some((f) => isDirtyValue(edits?.[warehouseId]?.[f], wh[f]));

          return (
            <div
              key={warehouseId}
              className="grid gap-3 px-4 py-2 items-center border-t"
              style={{
                gridTemplateColumns: '1.4fr 1fr 1fr 1fr',
                borderColor: UI_COLORS.BORDERS.LIGHT,
                backgroundColor: hasAnyDirty ? 'rgba(59, 130, 246, 0.04)' : 'transparent',
              }}
            >
              <div className="flex items-center gap-2 min-w-0">
                <FontAwesomeIcon icon={faWarehouse} className="text-xs flex-shrink-0" style={{ color: UI_COLORS.TEXT.MUTED }} />
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                    {warehouseName}
                  </div>
                  {isLow && (
                    <div className="text-[10px] font-semibold" style={{ color: UI_COLORS.STATUS.ERROR }}>
                      {perWH.lowStock}
                    </div>
                  )}
                </div>
              </div>
              {renderInput(warehouseId, 'stock_quantity', wh.stock_quantity)}
              {renderInput(warehouseId, 'minimum_stock', wh.minimum_stock)}
              {renderInput(warehouseId, 'pending_order', wh.pending_order)}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BulkEditWarehouseRows;
